/**
 * @fileoverview Every endpoint the KPI grid touches, as named functions.
 * Components never assemble a URL; if an endpoint moves, it moves here only.
 *
 * All routes sit under the super-admin base (riu_backend, newRoutes/Admin.js).
 */

import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config.json";

const ADMIN_API_URL = config.ADMIN_API_URL;
const API_URL = config.API_URL;

/**
 * One page of KPIs for a financial year.
 *
 * Points at `getKpiGrid`, not `getReportingQuestion`: the older endpoint
 * serves several screens from one function, branches on role and a `type`
 * flag, and interpolates its filters straight into SQL. This one is
 * parameterised, returns a single shape, and reports how many KPIs matched —
 * which the grid needs, because a year holds around 2,700 of them.
 *
 * @param {{ financialYearIds: (number|string)[], moduleIds: (number|string)[], questionTypes: string[], search: string, includeInactive?: boolean, limit: number, offset: number }} args
 */
export const getKpis = ({ financialYearIds, moduleIds, questionTypes, search, includeInactive, limit, offset }) =>
  apiCall(
    `${ADMIN_API_URL}getKpiGrid`,
    {},
    {
      financial_year_ids: (financialYearIds || []).join(","),
      module_ids: (moduleIds || []).join(","),
      question_types: (questionTypes || []).join(","),
      search: search || "",
      include_inactive: includeInactive ? 1 : 0,
      limit,
      offset,
    },
    "GET",
  );

/**
 * Upserts every changed and new row in one request.
 *
 * Points at `saveKpiGrid`, not `saveReportingQuestion`: the older endpoint
 * still serves the existing questions screen and cannot express what the grid
 * needs — a transaction around the whole batch, `question_id` so a rename is a
 * rename, and `module_id` per row. Both write the same tables, so the two can
 * converge later.
 *
 * @param {object} payload from helpers.buildSavePayload
 */
export const saveKpis = (payload) =>
  apiCall(`${ADMIN_API_URL}saveKpiGrid`, {}, payload, "POST", { silent: true });

/** `type` is mandatory on this endpoint — it 400s without it. */
export const getModules = () =>
  apiCall(`${ADMIN_API_URL}getQuestionModule`, {}, { type: "ALL" }, "GET");

/**
 * `iso_code` is mandatory: the controller branches on it and returns nothing
 * at all when it is absent, which surfaces as a 500.
 */
export const getFinancialYears = () =>
  apiCall(`${ADMIN_API_URL}getAllFinancialYear`, {}, { iso_code: "ALL" }, "GET");

/** Tag vocabulary — riu_keywords rows, one per `keyword_type:keyword_value`. */
export const getKeywords = () => apiCall(`${ADMIN_API_URL}getKeywords`, {}, {}, "GET");

/**
 * Adds one `keyword_type: keyword_value` pair. The endpoint is idempotent —
 * an existing pair comes back as a success.
 * @param {string} keywordType
 * @param {string} keywordValue
 * @param {{ silent?: boolean, esgPillar?: string|null }} [options] silent for bulk work, which reports its own
 * outcome; esgPillar only for a module pair
 */
export const createKeyword = (keywordType, keywordValue, { esgPillar, ...options } = {}) =>
  apiCall(
    `${ADMIN_API_URL}createKeyword`,
    {},
    esgPillar
      ? { keyword_type: keywordType, keyword_value: keywordValue, esg_pillar: esgPillar }
      : { keyword_type: keywordType, keyword_value: keywordValue },
    "POST",
    options,
  );

/**
 * Puts a module tag, in every spelling, under an ESG pillar; the tag applies it to every KPI it tags.
 * @param {string} moduleTag
 * @param {string} esgPillar ENVIRONMENT, SOCIAL or GOVERNANCE
 */
export const setModuleTagPillar = (moduleTag, esgPillar) =>
  apiCall(
    `${ADMIN_API_URL}setModuleTagPillar`,
    {},
    { keyword_value: moduleTag, esg_pillar: esgPillar },
    "POST",
    { silent: true },
  );

/** Every module tag with its ESG pillar and the number of active KPIs it tags. Silent: the panel shows its own error. */
export const getModuleTagPillars = () =>
  apiCall(`${ADMIN_API_URL}getModuleTagPillars`, {}, {}, "GET", { silent: true });

/** Reporting frameworks a KPI can be mapped to. `type` is mandatory. */
export const getFrameworks = () =>
  apiCall(`${ADMIN_API_URL}getFramework`, {}, { type: "ALL" }, "GET");

/** Industries a KPI can be restricted to. Lives outside the admin base. */
export const getIndustries = () =>
  apiCall(`${API_URL}api/v1/getAllIndustries`, {}, {}, "GET");

/** riu_unit_master — the units a KPI's conversion table can reference. */
export const getUnitMaster = () => apiCall(`${ADMIN_API_URL}getUnitMaster`, {}, {}, "GET");

/**
 * GHG factors for a set of KPIs. The endpoint takes `kpi_id` as a
 * comma-separated list and already orders by database priority, so the first
 * row returned for a unit is the one that would be applied.
 * @param {(number|string)[]} kpiIds
 */
export const getEmissionFactors = (kpiIds) =>
  apiCall(`${ADMIN_API_URL}getEmissionFactors`, {}, { kpi_id: kpiIds.join(",") }, "GET");

/** The GHG databases a factor may cite, in the priority order the reader applies. */
export const getGhgDatabases = () => apiCall(`${ADMIN_API_URL}getGhgDatabases`, {}, {}, "GET");

/**
 * Replaces the factor list of each KPI named in `items`. Every KPI whose
 * factors changed goes in one request, and the backend reconciles by primary
 * key inside a transaction — factors with an id are updated, those without are
 * inserted, and stored factors the list omits are deleted.
 *
 * @param {{ kpi_id: number, factors: object[] }[]} items
 */
export const saveEmissionFactors = (items) =>
  apiCall(`${ADMIN_API_URL}saveEmissionFactors`, {}, { items }, "POST", { silent: true });

/** The largest page getKpiGrid serves. */
const MAX_PAGE = 2000;

/**
 * Every KPI matching the filters, across all pages — for the Excel download
 * and for matching an upload against what is stored.
 *
 * @param {{ financialYearIds: (number|string)[], moduleIds?: (number|string)[], questionTypes?: string[], search?: string, includeInactive?: boolean }} filters
 * @returns {Promise<object[]>} raw rows, as getKpiGrid returns them
 */
export const getAllKpis = async (filters) => {
  const rows = [];
  for (let offset = 0; ; offset += MAX_PAGE) {
    const res = await getKpis({ moduleIds: [], questionTypes: [], search: "", ...filters, limit: MAX_PAGE, offset });
    const page = res?.data?.data;
    if (res?.isSuccess === false || !page) throw new Error(res?.data?.error || "Could not load KPIs");
    rows.push(...(page.rows || []));
    if (rows.length >= (page.total ?? 0) || !(page.rows || []).length) return rows;
  }
};

/** KPI ids per getEmissionFactors call — they travel in the query string. */
const FACTOR_ID_CHUNK = 300;

/**
 * Every stored factor for the given KPIs, fetched in URL-safe chunks.
 * Throws on any failure: callers replace factor lists, so a partial read must
 * never pass for "these KPIs have no factors".
 *
 * @param {(number|string)[]} kpiIds
 * @returns {Promise<object[]>} raw factor rows
 */
export const getAllEmissionFactors = async (kpiIds) => {
  const rows = [];
  for (let i = 0; i < kpiIds.length; i += FACTOR_ID_CHUNK) {
    const res = await getEmissionFactors(kpiIds.slice(i, i + FACTOR_ID_CHUNK));
    const data = res?.data?.data;
    if (res?.isSuccess === false || !Array.isArray(data)) {
      throw new Error(res?.data?.error || "Could not load emission factors");
    }
    rows.push(...data);
  }
  return rows;
};
